import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { ApplicationSession } from './application-session';

export function SessionWarning({ session, children }: { session: ApplicationSession; children: ReactNode }) {
  const view = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
  const dialog = useRef<HTMLDialogElement>(null);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (view.warning) { dialog.current?.showModal(); setError(false); }
    else dialog.current?.close();
  }, [view.warning]);

  if (view.expired) return <div role="status" className="p-6">Your session has expired. Please sign in again.</div>;
  return (
    <>
      {children}
      {view.warning && <dialog ref={dialog} aria-labelledby="session-warning-title" aria-describedby="session-warning-description"
        onCancel={(event) => event.preventDefault()}
        className="m-auto w-[min(92vw,440px)] rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl backdrop:bg-slate-950/50">
        <h2 id="session-warning-title" className="text-lg font-semibold">Your session is about to expire</h2>
        <p id="session-warning-description" className="mt-2 text-sm text-slate-600">
          {view.absolute ? 'Your 8-hour session limit is approaching. Save your work and sign in again.'
            : 'You have been inactive. Save your work or choose Stay signed in to continue.'}
        </p>
        <p className="mt-2 text-sm">Signing out in {view.seconds} seconds.</p>
        {error && <p role="alert" className="mt-2 text-sm text-red-700">Unable to extend your session. Check your connection and try again.</p>}
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="rounded-lg border px-3 py-2 text-sm" onClick={() => session.expire()}>Sign out</button>
          {!view.absolute && <button type="button" disabled={pending} className="rounded-lg bg-blue-700 px-3 py-2 text-sm text-white disabled:opacity-60"
            onClick={async () => {
              setPending(true);
              try { await session.staySignedIn(); setError(false); }
              catch { setError(true); }
              finally { setPending(false); }
            }}>Stay signed in</button>}
        </div>
      </dialog>}
    </>
  );
}
